/**
 * nav-panel.js
 *
 * Desktop side-panel navigation for Sarah Haran theme.
 * Manages the open/close state of the fixed-position nav panels rendered by
 * header-drawer.liquid (Sections B + C).
 *
 * Relies on trapFocus / removeTrapFocus defined in global.js (loaded before
 * this script via theme.liquid deferred script order).
 *
 * No custom elements are defined here — this avoids conflicts with the existing
 * HeaderDrawer / MenuDrawer custom elements in global.js.
 */

(function () {
  'use strict';

  // ─── State ────────────────────────────────────────────────────────────────
  let activePanel = null;    // currently open .nav-panel element
  let activeTrigger = null;  // currently active [data-nav-trigger] button

  // ─── DOM refs (populated in init) ─────────────────────────────────────────
  let sectionHeader = null;
  let backdrop = null;
  let borderOffset = 0;

  // ─── Helpers ──────────────────────────────────────────────────────────────

  function getScrollbarWidth() {
    return window.innerWidth - document.documentElement.clientWidth;
  }

  function getHeaderBottom() {
    if (!sectionHeader) return 0;
    // Math.round to avoid sub-pixel gaps between header and panel
    return Math.round(sectionHeader.getBoundingClientRect().bottom - borderOffset);
  }

  function setCssVars() {
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${getHeaderBottom()}px`
    );
    document.documentElement.style.setProperty(
      '--viewport-height',
      `${window.innerHeight}px`
    );
  }

  // ─── Open / close ─────────────────────────────────────────────────────────

  function openPanel(panel, trigger) {
    setCssVars();

    activePanel = panel;
    activeTrigger = trigger;

    panel.classList.add('nav-panel--open');
    panel.setAttribute('aria-hidden', 'false');

    trigger.setAttribute('aria-expanded', 'true');

    backdrop.classList.add('nav-panel-backdrop--visible');
    backdrop.setAttribute('aria-hidden', 'false');

    sectionHeader.classList.add('menu-open');

    // Dim inactive nav triggers
    const navEl = document.querySelector('.header__desktop-nav');
    if (navEl) navEl.classList.add('header__desktop-nav--panel-open');

    // Compensate for scrollbar disappearing so page doesn't shift right
    const scrollbarWidth = getScrollbarWidth();
    if (scrollbarWidth > 0) document.body.style.paddingRight = scrollbarWidth + 'px';
    document.body.classList.add('overflow-hidden-desktop');

    // Close when clicking anywhere outside the panel or its trigger
    document.addEventListener('mousedown', onDocumentMousedown);

    window.addEventListener('resize', onResize);

    // Trap focus inside the panel; focus the close button first
    const closeBtn = panel.querySelector('.nav-panel__close');
    if (typeof trapFocus === 'function') {
      trapFocus(panel, closeBtn || panel);
    }
  }

  function closeAll() {
    if (!activePanel) return;

    // Capture references before clearing state (avoids race conditions)
    const panelToClose = activePanel;
    const triggerToFocus = activeTrigger;

    activePanel = null;
    activeTrigger = null;

    panelToClose.classList.remove('nav-panel--open');
    panelToClose.setAttribute('aria-hidden', 'true');

    if (triggerToFocus) {
      triggerToFocus.setAttribute('aria-expanded', 'false');
    }

    backdrop.classList.remove('nav-panel-backdrop--visible');
    backdrop.setAttribute('aria-hidden', 'true');

    sectionHeader.classList.remove('menu-open');

    // Restore all nav trigger opacity
    const navEl = document.querySelector('.header__desktop-nav');
    if (navEl) navEl.classList.remove('header__desktop-nav--panel-open');

    document.body.classList.remove('overflow-hidden-desktop');
    document.body.style.paddingRight = '';

    document.removeEventListener('mousedown', onDocumentMousedown);

    window.removeEventListener('resize', onResize);

    // Return focus to the trigger that opened the panel
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(triggerToFocus);
    } else if (triggerToFocus) {
      triggerToFocus.focus();
    }
  }

  /**
   * Switch directly from one open panel to another without touching the
   * backdrop, dimming class, or menu-open class — those stay constant so the
   * overlay and dimming never flash.
   *
   * Both panels get `nav-panel--switching` to suppress all CSS transitions,
   * making the swap truly instant (no content fade, no opacity animation).
   * A forced reflow commits the changes before transitions are re-enabled.
   */
  function switchPanel(panel, trigger) {
    setCssVars();

    const oldPanel = activePanel;
    const oldTrigger = activeTrigger;

    // Suppress all transitions on both panels for an instant swap
    oldPanel.classList.add('nav-panel--switching');
    panel.classList.add('nav-panel--switching');

    // Close old panel
    oldPanel.classList.remove('nav-panel--open');
    oldPanel.setAttribute('aria-hidden', 'true');

    // Open new panel
    panel.classList.add('nav-panel--open');
    panel.setAttribute('aria-hidden', 'false');

    // Swap trigger highlight — set new BEFORE clearing old so there is
    // never a frame where no trigger has aria-expanded="true"
    trigger.setAttribute('aria-expanded', 'true');
    oldTrigger.setAttribute('aria-expanded', 'false');

    // Update state refs
    activePanel = panel;
    activeTrigger = trigger;

    // Force reflow to commit all style changes, then re-enable transitions
    void panel.offsetHeight;
    oldPanel.classList.remove('nav-panel--switching');
    panel.classList.remove('nav-panel--switching');

    // Move focus to new panel
    if (typeof removeTrapFocus === 'function') removeTrapFocus();
    const closeBtn = panel.querySelector('.nav-panel__close');
    if (typeof trapFocus === 'function') trapFocus(panel, closeBtn || panel);
  }

  // ─── Event handlers ───────────────────────────────────────────────────────

  function onTriggerClick(event) {
    const trigger = event.currentTarget;
    const panelId = trigger.getAttribute('aria-controls');
    const panel = document.getElementById(panelId);
    if (!panel) return;

    // Already viewing this panel — do nothing
    if (activePanel === panel) {
      return;
    }

    // If another panel is already open, swap without disturbing backdrop/dimming
    if (activePanel) {
      switchPanel(panel, trigger);
      return;
    }

    // No panel open yet — full open
    openPanel(panel, trigger);
  }

  function onKeyUp(event) {
    if (event.key !== 'Escape') return;
    if (!activePanel) return;
    closeAll();
  }

  function onBackdropClick() {
    closeAll();
  }

  /**
   * Close panel when the user presses down outside of it.
   * Using mousedown (not click) gives an immediate response and avoids
   * races with click events on triggers.
   */
  function onDocumentMousedown(event) {
    if (!activePanel) return;
    // Click inside the open panel — do nothing
    if (activePanel.contains(event.target)) return;
    // Click anywhere inside the header bar (nav items, gaps, padding, logo, icons)
    // — must not close when clicking within the sticky header wrapper
    if (event.target.closest('.header-wrapper')) return;
    closeAll();
  }

  function onCloseBtnClick() {
    closeAll();
  }

  function onResize() {
    if (!activePanel) return;
    setCssVars();
  }

  // ─── Init ─────────────────────────────────────────────────────────────────

  function init() {
    sectionHeader = document.querySelector('.section-header');
    if (!sectionHeader) return;

    // Calculate border offset (mirrors HeaderDrawer in global.js)
    const headerWrapper = document.querySelector('.header-wrapper');
    borderOffset = headerWrapper && headerWrapper.classList.contains('header-wrapper--border-bottom') ? 1 : 0;

    backdrop = document.querySelector('.nav-panel-backdrop');
    if (!backdrop) return;

    // Move panels and backdrop to document.body so they sit in the root stacking context.
    // The .section-header wrapper has z-index: 3 (shopify-section-group-header-group), creating
    // a stacking context that traps children — panels and backdrop must be root-level siblings
    // so their own z-index values (panel: 4, backdrop: 3) compare directly against each other.
    document.querySelectorAll('.nav-panel').forEach(function (panel) {
      document.body.appendChild(panel);
    });
    document.body.appendChild(backdrop);

    // Wire up trigger buttons (desktop nav)
    document.querySelectorAll('[data-nav-trigger]').forEach(function (trigger) {
      trigger.addEventListener('click', onTriggerClick);
    });

    // Wire up close buttons inside panels
    document.querySelectorAll('.nav-panel__close').forEach(function (btn) {
      btn.addEventListener('click', onCloseBtnClick);
    });

    // Backdrop click
    backdrop.addEventListener('click', onBackdropClick);

    // ESC key (document-level)
    document.addEventListener('keyup', onKeyUp);

  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
